// src/lib/api.js
import {
  API_BASE_URL,
  API_ENDPOINTS,
  buildQueryString,
} from "../config/api.config";

// Export for backward compatibility
export const API = API_BASE_URL;

/* -------- core fetch helper -------- */
async function apiFetch(url, options = {}) {
  // Get the JWT token from localStorage
  const token = localStorage.getItem("bookatherapy_token");

  // Build headers with Authorization if token exists
  // Don't set Content-Type for FormData - browser will set it automatically with boundary
  const headers = {
    ...(options.headers || {}),
  };

  // Only set Content-Type if body is not FormData
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    ...options,
    headers,
    credentials: "include",
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      msg = body?.message || msg;
    } catch {
      /* noop */
    }
    const err = new Error(msg);
    err.status = res.status;
    err.url = url;
    throw err;
  }
  return res.json().catch(() => ({}));
}

/* -------- match preview helpers (you already added) -------- */
export async function previewOne(payload) {
  // Log the payload being sent to the API
  console.log("📤 Sending to /match/preview:", payload);

  // Use apiFetch to ensure Authorization header is included
  return apiFetch(`${API}/match/preview`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function previewAll({
  dateISO,
  timeExact,
  timePref,
  flexibleTime,
  persona,
  clientPostalCode,
  serviceId,
}) {
  const settings = ["Home", "Mobile", "Clinic"];
  const base = {
    dateISO: dateISO || null,
    timeExact: timeExact || null,
    timePref: timePref || null,
    flexibleTime: !!flexibleTime,
    persona: persona || null,
    clientPostalCode: clientPostalCode || null, // Add user's postal code for distance filtering
    serviceId: serviceId || null, // Service ID to filter therapists by massage type
  };

  console.log("📤 previewAll base payload:", base);

  const out = {};
  await Promise.all(
    settings.map(async (s) => {
      try {
        const data = await previewOne({ ...base, setting: s });
        out[s] = data?.results || [];
      } catch {
        out[s] = [];
      }
    }),
  );
  return out; // { Home:[], Mobile:[], Clinic:[] }
}

/* -------- other endpoints used by Matches/Confirm/BookingFlow -------- */

// Counts: GET /therapists/counts-by-setting  -> { Home, Mobile, Clinic }
async function countsBySetting() {
  return apiFetch(`${API}/therapists/counts-by-setting`);
}

// Inline slots card: GET /availability/slots?setting=..&date=YYYY-MM-DD -> { slots: [] }
async function daySlots({ setting, date }) {
  const qs = new URLSearchParams({ setting, date });
  const data = await apiFetch(`${API}/availability/slots?${qs.toString()}`);
  return Array.isArray(data?.slots) ? data.slots : [];
}

// Therapist details (for avatar, etc.): GET /therapists/:id
async function getTherapist(therapistId) {
  return apiFetch(`${API}/therapists/${therapistId}`);
}

// Get therapist slots: GET /availability/:therapistId/slots?date=YYYY-MM-DD&setting=X
async function therapistSlots(therapistId, date, setting) {
  const params = { date };
  if (setting) {
    params.setting = setting;
  }
  const qs = new URLSearchParams(params);
  try {
    // Backend uses /availability/:therapistId/slots endpoint
    return await apiFetch(
      `${API}/availability/${therapistId}/slots?${qs.toString()}`,
    );
  } catch (err) {
    // Gracefully handle if endpoint doesn't exist or returns 404
    if (err.status === 404) {
      return { slots: [] };
    }
    throw err;
  }
}

// Review-based matching: GET /therapists/by-reviews?setting=X&serviceId=Y&date=Z
async function matchByReviews(params) {
  const { setting, serviceId, dateISO, location } = params;
  const qs = new URLSearchParams();
  if (setting) qs.append("setting", setting);
  if (serviceId) qs.append("serviceId", serviceId);
  if (dateISO) qs.append("date", dateISO);
  if (location) {
    if (location.lat) qs.append("lat", location.lat);
    if (location.lng) qs.append("lng", location.lng);
  }
  return apiFetch(`${API}/therapists/by-reviews?${qs.toString()}`);
}

// Hold a slot: POST /booking/hold
// body: { therapistId, setting, dateISO, time, persona?, clientEmail?, clientName? }
async function createHold(body) {
  return apiFetch(`${API}/bookings/hold`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// Insurance Details API
async function getInsuranceProviders() {
  return apiFetch(`${API}/client/insurance-details/providers`);
}

async function createInsuranceDetails(data) {
  return apiFetch(`${API}/client/insurance-details`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

async function getInsuranceDetails({ id, email, bookingId, clientId }) {
  const params = new URLSearchParams();
  if (id) params.append("id", id);
  if (email) params.append("email", email);
  if (bookingId) params.append("bookingId", bookingId);
  if (clientId) params.append("clientId", clientId);
  return apiFetch(`${API}/client/insurance-details?${params.toString()}`);
}

// Intake Form API
async function createIntakeForm(data) {
  return apiFetch(`${API}/client/intake-form`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

async function getIntakeForm({ id, email, bookingId, clientId }) {
  const params = new URLSearchParams();
  if (id) params.append("id", id);
  if (email) params.append("email", email);
  if (bookingId) params.append("bookingId", bookingId);
  if (clientId) params.append("clientId", clientId);
  return apiFetch(`${API}/client/intake-form?${params.toString()}`);
}

// Upload insurance card
async function uploadInsuranceCard(file) {
  const formData = new FormData();
  formData.append("file", file);
  return apiFetch(`${API}/uploads/insurance-card`, {
    method: "POST",
    body: formData,
  });
}

// Confirm hold: POST /booking/confirm  body: { holdId, paymentInfo, referralCode?, giftCardCode?, giftCardDiscount?, useFreeMassage?, totalAmount?, servicePrice?, addOnsPrice? }
// Confirm hold: POST /booking/confirm  body: { holdId, paymentInfo, referralCode?, giftCardCode?, giftCardDiscount?, totalAmount?, serviceDetails? }
async function confirmHold({
  holdId,
  paymentInfo,
  referralCode,
  giftCardCode,
  giftCardDiscount,
  useFreeMassage,
  totalAmount,
  serviceDetails,
  addOnsPrice,
}) {
  return apiFetch(`${API}/bookings/confirm`, {
    method: "POST",
    body: JSON.stringify({
      holdId,
      paymentInfo,
      referralCode,
      giftCardCode,
      giftCardDiscount,
      useFreeMassage,
      totalAmount,
      serviceDetails,
      addOnsPrice,
    }),
  });
}

// Favorite toggle (if you add it later)
// async function toggleFavorite(therapistId, isFav) { ... }

/* -------- export an api object (to satisfy existing imports) -------- */
export const api = {
  previewOne,
  previewAll,
  matchByReviews,
  countsBySetting,
  daySlots,
  therapist: getTherapist,
  therapistSlots,

  // booking flow
  createHold,
  confirmHold,

  // aliases for old code
  hold: createHold,
  confirm: confirmHold,
};

/* -------- Gift Card API endpoints -------- */

// Create payment intent for gift card
async function createGiftCardPaymentIntent({ amount, senderEmail }) {
  return apiFetch(`${API}/gift-cards/payment-intent`, {
    method: "POST",
    body: JSON.stringify({ amount, senderEmail }),
  });
}

// Create gift card after successful payment
async function createGiftCard(giftCardData) {
  return apiFetch(`${API}/gift-cards/create`, {
    method: "POST",
    body: JSON.stringify(giftCardData),
  });
}

// Verify gift card by code
async function verifyGiftCard(code) {
  return apiFetch(`${API}/gift-cards/verify/${code}`);
}

// Validate gift card for booking (returns isValid, amount, currency)
async function validateGiftCardForBooking(code) {
  return apiFetch(`${API}/gift-cards/${code}/validate`);
}

// Redeem gift card
async function redeemGiftCard(code) {
  return apiFetch(`${API}/gift-cards/redeem`, {
    method: "POST",
    body: JSON.stringify({ giftCardCode: code }),
  });
}

// Search massage clinics
async function searchMassageClinics(query) {
  const qs = new URLSearchParams({ query });
  return apiFetch(`${API}/massage-clinics/search?${qs.toString()}`);
}

// Get gift card by ID (for order history)
async function getGiftCard(giftCardId) {
  return apiFetch(`${API}/gift-cards/${giftCardId}`);
}

// Get user's gift card purchases
async function getUserGiftCards() {
  return apiFetch(`${API}/gift-cards/my-purchases`);
}

// Add gift card endpoints to api object
api.giftCards = {
  createPaymentIntent: createGiftCardPaymentIntent,
  create: createGiftCard,
  verify: verifyGiftCard,
  validateForBooking: validateGiftCardForBooking,
  redeem: redeemGiftCard,
  getById: getGiftCard,
  getMyPurchases: getUserGiftCards,
};

api.massageClinics = {
  search: searchMassageClinics,
};

/* -------- Loyalty Program API endpoints -------- */

// Get or create loyalty program
async function getLoyaltyProgram(email, userId) {
  const qs = new URLSearchParams({ email, userId });
  return apiFetch(`${API}/loyalty/program?${qs.toString()}`);
}

// Verify referral code
async function verifyReferralCode(code) {
  return apiFetch(`${API}/loyalty/verify-referral/${code}`);
}

// Apply referral code
async function applyReferralCode(referralCode, newUserEmail, newUserId) {
  return apiFetch(`${API}/loyalty/apply-referral`, {
    method: "POST",
    body: JSON.stringify({ referralCode, newUserEmail, newUserId }),
  });
}

// Award points for booking
async function awardPoints(bookingId, userEmail, points = 1) {
  return apiFetch(`${API}/loyalty/award-points`, {
    method: "POST",
    body: JSON.stringify({ bookingId, userEmail, points }),
  });
}

// Redeem free massage
async function redeemFreeMassage(userEmail, bookingId) {
  return apiFetch(`${API}/loyalty/redeem`, {
    method: "POST",
    body: JSON.stringify({ userEmail, bookingId }),
  });
}

// Get loyalty stats
async function getLoyaltyStats(email) {
  const qs = new URLSearchParams({ email });
  return apiFetch(`${API}/loyalty/stats?${qs.toString()}`);
}

// Get leaderboard
async function getLeaderboard() {
  return apiFetch(`${API}/loyalty/leaderboard`);
}

// Add loyalty endpoints to api object
api.loyalty = {
  getProgram: getLoyaltyProgram,
  verifyReferralCode,
  applyReferralCode,
  awardPoints,
  redeemFreeMassage,
  getStats: getLoyaltyStats,
  getLeaderboard,
};

// Get saved payment methods for user by email
async function getSavedPaymentMethod(email) {
  if (!email) {
    throw new Error("Email is required to fetch saved payment methods");
  }

  try {
    const data = await apiFetch(
      `${API}/payment/saved-method/${encodeURIComponent(email)}`,
    );
    const paymentMethods = data?.paymentMethods || [];

    if (paymentMethods.length === 0) {
      return { paymentMethods: [], paymentMethod: null, isDefault: false };
    }

    // Try to get customer to find default payment method
    try {
      const customerData = await apiFetch(
        `${API}/payment/customer/${encodeURIComponent(email)}`,
      );
      const defaultPaymentMethodId =
        customerData?.customer?.invoice_settings?.default_payment_method;

      if (defaultPaymentMethodId) {
        const defaultPaymentMethod = paymentMethods.find(
          (pm) => pm.id === defaultPaymentMethodId,
        );
        if (defaultPaymentMethod) {
          return {
            paymentMethods,
            paymentMethod: defaultPaymentMethod,
            isDefault: true,
          };
        }
      }
    } catch (error) {
      console.warn(
        "Could not fetch customer details for default payment method:",
        error,
      );
    }

    // If no default found, return the first payment method
    return {
      paymentMethods,
      paymentMethod: paymentMethods[0],
      isDefault: false,
    };
  } catch (error) {
    // If endpoint returns 404, customer doesn't exist or has no payment methods
    if (error.status === 404) {
      return { paymentMethods: [], paymentMethod: null, isDefault: false };
    }
    throw error;
  }
}

// Update payment method (creates setup intent for updating)
async function createUpdateIntent(clientEmail, clientName) {
  return apiFetch(`${API}/payment/setup-intent`, {
    method: "POST",
    body: JSON.stringify({
      clientEmail,
      clientName,
    }),
  });
}

// Rewards API functions
async function getRewards() {
  return apiFetch(`${API}/rewards`);
}

async function getRewardsHistory(page = 1, limit = 20) {
  const qs = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });
  return apiFetch(`${API}/rewards/history?${qs.toString()}`);
}

async function checkRedemptionEligibility() {
  return apiFetch(`${API}/rewards/check-eligibility`);
}

async function redeemFreeMassageReward({ bookingId }) {
  return apiFetch(`${API}/rewards/redeem`, {
    method: "POST",
    body: JSON.stringify({ bookingId }),
  });
}

// Referral API functions
async function getOrCreateReferralCode() {
  return apiFetch(`${API}/referral/code`);
}

async function validateReferralCode(referralCode) {
  return apiFetch(
    `${API}/referral/validate/${encodeURIComponent(referralCode)}`,
  );
}

async function getReferralHistory(page = 1, limit = 20) {
  const qs = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });
  return apiFetch(`${API}/referral/history?${qs.toString()}`);
}

// Add payment endpoints to api object
api.payment = {
  getPublishableKey: async () => {
    return apiFetch(`${API}/payment/publishable-key`);
  },
  getSavedPaymentMethod,
  createUpdateIntent,
};

// Add rewards endpoints to api object
api.rewards = {
  getRewards,
  getRewardsHistory,
  checkRedemptionEligibility,
  redeemFreeMassageReward,
};

// Add referral endpoints to api object
api.referral = {
  getOrCreateReferralCode,
  validateReferralCode,
  getReferralHistory,
};

/* -------- User Profile API endpoints -------- */

// Get user profile by email
async function getUserProfile(email) {
  const qs = new URLSearchParams({ email });
  try {
    return await apiFetch(`${API}/user/profile?${qs.toString()}`);
  } catch (err) {
    // Gracefully handle if profile doesn't exis  t
    if (err.status === 404) {
      return null;
    }
    throw err;
  }
}

// Save/Update user profile
async function saveUserProfile({ email, fullName, phone }) {
  try {
    return await apiFetch(`${API}/user/profile`, {
      method: "POST",
      body: JSON.stringify({ email, fullName, phone }),
    });
  } catch (err) {
    // Gracefully handle errors
    if (err.status === 404) {
      return { success: false, message: "User not found" };
    }
    throw err;
  }
}

// Get user's favorite therapists
async function getFavoriteTherapists() {
  return apiFetch(`${API}/user/favorite-therapists`);
}

// Add a therapist to favorites
async function addFavoriteTherapist(therapistId) {
  return apiFetch(`${API}/user/favorite-therapists/${therapistId}`, {
    method: "POST",
  });
}

// Remove a therapist from favorites
async function removeFavoriteTherapist(therapistId) {
  return apiFetch(`${API}/user/favorite-therapists/${therapistId}`, {
    method: "DELETE",
  });
}

// Add user profile endpoints to api object
api.user = {
  getProfile: getUserProfile,
  saveProfile: saveUserProfile,
  getFavoriteTherapists,
  addFavoriteTherapist,
  removeFavoriteTherapist,
};

/* -------- Appointments API endpoints -------- */

// Get user's appointments (upcoming and history)
async function getMyAppointments() {
  return apiFetch(`${API}/bookings/my-appointments`);
}

// Cancel an appointment by ID
async function cancelAppointment(bookingId) {
  return apiFetch(`${API}/bookings/${bookingId}/cancel`, {
    method: "POST",
  });
}

// Reschedule an appointment
async function rescheduleAppointment(bookingId, newDateTime) {
  return apiFetch(`${API}/bookings/${bookingId}/reschedule`, {
    method: "POST",
    body: JSON.stringify({ appointmentDate: newDateTime }),
  });
}

// Update appointment using PUT (like page.tsx)
async function updateAppointment(bookingId, payload) {
  return apiFetch(`${API}/bookings/clinic/${bookingId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

// Create a new booking (direct booking from dashboard)
async function createBooking(payload) {
  return apiFetch(`${API}/bookings/clinic`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// Get therapist reviews
async function getTherapistReviews(therapistId) {
  return apiFetch(`${API}/therapists/${therapistId}/reviews`);
}

// Fetch therapist availability for multiple dates
async function fetchTherapistAvailability(therapistId, startDate, endDate) {
  const dates = [];
  const current = new Date(startDate);
  const end = new Date(endDate);

  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  const availabilityPromises = dates.map(async (date) => {
    const dateStr = date.toISOString().split("T")[0];
    try {
      const data = await therapistSlots(therapistId, dateStr);
      return {
        date: dateStr,
        slots: data?.slots || [],
      };
    } catch (error) {
      console.warn(`Failed to fetch slots for ${dateStr}:`, error);
      return {
        date: dateStr,
        slots: [],
      };
    }
  });

  const results = await Promise.all(availabilityPromises);
  return results.reduce((acc, { date, slots }) => {
    acc[date] = slots;
    return acc;
  }, {});
}

// Fetch therapist away times
async function fetchTherapistAways(therapistId) {
  try {
    const res = await apiFetch(`${API}/therapists/aways/${therapistId}`);
    if (!res || !Array.isArray(res)) {
      return [];
    }
    return res || [];
  } catch (error) {
    console.warn("Failed to load away times:", error);
    return [];
  }
}

// Fetch therapist appointments for a specific date
async function fetchTherapistAppointments(therapistId, date) {
  try {
    const dateStr =
      typeof date === "string" ? date : date.toISOString().split("T")[0];
    const res = await apiFetch(
      `${API}/therapist-portal/${therapistId}/appointments?date=${dateStr}`,
    );
    return res?.appointments || [];
  } catch (error) {
    console.warn("Failed to fetch therapist appointments:", error);
    return [];
  }
}

// Get receipt data for a booking
async function getBookingReceipt(bookingId) {
  return apiFetch(`${API}/bookings/${bookingId}/receipt`, {
    method: "GET",
  });
}

// Email receipt to client
async function emailBookingReceipt(bookingId, pdfBase64) {
  return apiFetch(`${API}/bookings/${bookingId}/email-receipt`, {
    method: "POST",
    body: JSON.stringify({ pdfBase64 }),
  });
}

// Send appointment confirmation email to client
async function sendAppointmentEmail(bookingId) {
  return apiFetch(`${API}/bookings/${bookingId}/send-confirmation`, {
    method: "POST",
  });
}

export async function sendAppointmentConfirmationEmail(bookingId) {
  return apiFetch(`${API}/bookings/${bookingId}/send-confirmation`, {
    method: "POST",
  });
}

// Reassignment endpoints
async function getPendingReassignments() {
  return apiFetch(`${API}/bookings/reassignment/pending`);
}

async function acceptReassignment(bookingId) {
  return apiFetch(`${API}/bookings/${bookingId}/reassignment/accept`, {
    method: "POST",
  });
}

async function rejectReassignment(bookingId) {
  return apiFetch(`${API}/bookings/${bookingId}/reassignment/reject`, {
    method: "POST",
  });
}

// Add appointments endpoints to api object
api.appointments = {
  getMyAppointments,
  cancelAppointment,
  rescheduleAppointment,
  updateAppointment,
  fetchTherapistAvailability,
  fetchTherapistAways,
  fetchTherapistAppointments,
  sendAppointmentEmail,
  getPendingReassignments,
  acceptReassignment,
  rejectReassignment,
};

// Also add to bookings object for consistency
api.bookings = {
  ...api.bookings,
  getPendingReassignments,
  acceptReassignment,
  rejectReassignment,
};

// Add receipt endpoints to api object
api.receipt = {
  getBookingReceipt,
  emailBookingReceipt,
};

// Add booking endpoints to api object
api.booking = {
  getAppointments: getMyAppointments,
  createBooking,
  cancelAppointment,
  rescheduleAppointment,
  updateAppointment,
};

// Add therapist endpoints to api object
api.therapist = {
  getTherapistReviews,
};

/* -------- AI Chatbot API endpoints -------- */

// Send message to AI chatbot
async function sendChatMessage({ messages, systemPrompt }) {
  return apiFetch(`${API}/chatbot/message`, {
    method: "POST",
    body: JSON.stringify({ messages, systemPrompt }),
  });
}

// Get chatbot training prompt
async function getChatbotTraining() {
  try {
    return await apiFetch(`${API}/chatbot/training`);
  } catch (err) {
    if (err.status === 404) {
      return { prompt: null };
    }
    throw err;
  }
}

// Update chatbot training prompt
async function updateChatbotTraining(prompt) {
  return apiFetch(`${API}/chatbot/training`, {
    method: "POST",
    body: JSON.stringify({ prompt }),
  });
}

// Add chatbot endpoints to api object
api.chatbot = {
  sendMessage: sendChatMessage,
  getTraining: getChatbotTraining,
  updateTraining: updateChatbotTraining,
};

/* -------- Pricing API endpoints -------- */

// Get all active services with pricing
async function getAllServices() {
  return apiFetch(`${API}/pricing/services`);
}

// Get all active add-ons
async function getAllAddOns() {
  return apiFetch(`${API}/pricing/addons`);
}

// Get full pricing catalog (services + addons)
async function getPricingCatalog() {
  return apiFetch(`${API}/pricing/catalog`);
}

// Calculate booking price
async function calculateBookingPrice({ serviceName, duration, addOns }) {
  return apiFetch(`${API}/pricing/calculate`, {
    method: "POST",
    body: JSON.stringify({ serviceName, duration, addOns }),
  });
}

// Add pricing endpoints to api object
api.pricing = {
  getServices: getAllServices,
  getAddOns: getAllAddOns,
  getCatalog: getPricingCatalog,
  calculatePrice: calculateBookingPrice,
};

// Add insurance and intake form endpoints to api object
api.insurance = {
  getProviders: getInsuranceProviders,
  create: createInsuranceDetails,
  get: getInsuranceDetails,
  uploadCard: uploadInsuranceCard,
};

api.intake = {
  create: createIntakeForm,
  get: getIntakeForm,
};

/* -------- Blog API endpoints (proxied through backend) -------- */

// Blog API now goes through our backend proxy to avoid CORS issues
// The backend will handle calling babylovegrowth.ai with the proper API key

// Get uploaded (locally-authored) published posts
async function getUploadedPosts() {
  try {
    const response = await apiFetch(`${API}/blog/posts`);
    return Array.isArray(response) ? response : [];
  } catch (error) {
    console.error("[Blog API] Error in getUploadedPosts:", error);
    return [];
  }
}

// Get all blog articles: uploaded posts first, then the external (proxied) feed.
async function getAllArticles() {
  // Fetch both sources in parallel; never let one failing source break the page.
  const [uploaded, external] = await Promise.all([
    getUploadedPosts(),
    apiFetch(`${API}/blog/articles`)
      .then((r) => (Array.isArray(r) ? r : []))
      .catch((err) => {
        console.error("[Blog API] external articles failed:", err);
        return [];
      }),
  ]);

  // Dedupe by id/slug so a post never appears twice. Uploaded wins.
  const seen = new Set(uploaded.map((p) => p.id || p.slug));
  const externalFiltered = external.filter((a) => !seen.has(a.id || a.slug));

  return [...uploaded, ...externalFiltered];
}

// Get single blog article by ID/slug. Try uploaded posts first, then external.
async function getArticleById(articleId) {
  try {
    if (!articleId) {
      throw new Error("Article ID is required");
    }

    // Check if this is the static blog slug
    if (articleId === "how-to-find-a-good-massage-therapist-home-visits" ||
        articleId === "how-to-find-massage-therapist") {
      const { getStaticBlog } = await import("../utils/staticBlog");
      return getStaticBlog();
    }

    // Try an uploaded post first (article id === slug for uploaded posts)
    try {
      return await apiFetch(`${API}/blog/posts/${articleId}`);
    } catch (uploadedErr) {
      if (uploadedErr?.status && uploadedErr.status !== 404) throw uploadedErr;
      // 404 -> fall back to the external proxy
    }

    console.log(`[Blog API] Fetching article ${articleId} via backend proxy`);
    return await apiFetch(`${API}/blog/articles/${articleId}`);
  } catch (error) {
    console.error(`[Blog API] Error in getArticleById for ID ${articleId}:`, error);
    throw error;
  }
}

// Get backlinks (via backend proxy)
async function getBacklinks() {
  return apiFetch(`${API}/blog/backlinks`);
}

// Get GEO audit issues (via backend proxy)
async function getGeoAuditIssues() {
  return apiFetch(`${API}/blog/geo-audit/issues`);
}

// Get RSS feed (via backend proxy)
async function getRssFeed() {
  return apiFetch(`${API}/blog/rss`);
}

// Add blog endpoints to api object
api.blog = {
  getAllArticles,
  getArticleById,
  getUploadedPosts,
  getBacklinks,
  getGeoAuditIssues,
  getRssFeed,
};

/* ============================================================================
 * Blog admin (OTP auth + post management). Uses a separate `blogadmin_token`.
 * ==========================================================================*/

const BLOG_ADMIN_TOKEN_KEY = "blogadmin_token";

export function getBlogAdminToken() {
  return localStorage.getItem(BLOG_ADMIN_TOKEN_KEY);
}
export function setBlogAdminToken(token) {
  if (token) localStorage.setItem(BLOG_ADMIN_TOKEN_KEY, token);
}
export function clearBlogAdminToken() {
  localStorage.removeItem(BLOG_ADMIN_TOKEN_KEY);
}

// fetch helper that authenticates with the blog admin token
async function adminFetch(url, options = {}) {
  const token = getBlogAdminToken();
  const headers = { ...(options.headers || {}) };
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(url, { ...options, headers, credentials: "include" });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      msg = body?.message || msg;
    } catch {
      /* noop */
    }
    const err = new Error(msg);
    err.status = res.status;
    err.url = url;
    throw err;
  }
  return res.json().catch(() => ({}));
}

api.blogAdmin = {
  requestOtp: (email) =>
    apiFetch(`${API}/blog/admin/login`, {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  verifyOtp: (userId, otp) =>
    apiFetch(`${API}/blog/admin/verify-otp`, {
      method: "POST",
      body: JSON.stringify({ userId, otp }),
    }),
  resendOtp: (userId) =>
    apiFetch(`${API}/blog/admin/resend-otp`, {
      method: "POST",
      body: JSON.stringify({ userId }),
    }),
  me: () => adminFetch(`${API}/blog/admin/me`),
  logout: () => adminFetch(`${API}/blog/admin/logout`, { method: "POST" }),

  listPosts: () => adminFetch(`${API}/blog/admin/posts`),
  createPost: (payload) =>
    adminFetch(`${API}/blog/admin/posts`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updatePost: (id, payload) =>
    adminFetch(`${API}/blog/admin/posts/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  deletePost: (id) =>
    adminFetch(`${API}/blog/admin/posts/${id}`, { method: "DELETE" }),
  uploadImage: (file) => {
    const form = new FormData();
    form.append("image", file);
    return adminFetch(`${API}/blog/admin/upload-image`, {
      method: "POST",
      body: form,
    });
  },
};

// Default export too (handy if some files do `import api from ...`)
export default api;
